/**
 * AssignPackageModal — pick a package and create an entitlement for a student.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Button, Modal } from '@/components/common';
import { ApiError } from '@core/api/client';
import { useCreateEntitlement } from '@features/catalog/api/entitlements';
import { usePackages } from '@features/catalog/api/packages';
import { formatPaise } from '@features/catalog/config/ui';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
}

export const AssignPackageModal: React.FC<Props> = ({ isOpen, onClose, studentId }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading } = usePackages({ status: 'active' });
  const createMut = useCreateEntitlement();

  const submit = async () => {
    if (!selected) {
      toast.error('Pick a package to assign');
      return;
    }
    try {
      await createMut.mutateAsync({
        student_id: studentId,
        package_id: selected,
        source: { type: 'manual' },
      });
      toast.success('Package assigned');
      setSelected(null);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to assign package';
      toast.error(msg);
    }
  };

  const packages = data?.items ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Package"
      size="md"
      data-testid="candeza-catalog-assign-package-modal"
    >
      {isLoading ? (
        <div data-testid="candeza-catalog-assign-package-loading">Loading packages…</div>
      ) : packages.length === 0 ? (
        <div data-testid="candeza-catalog-assign-package-empty" style={{ color: '#64748b' }}>
          No active packages available. Create one in Catalog first.
        </div>
      ) : (
        <div
          data-testid="candeza-catalog-assign-package-list"
          style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}
        >
          {packages.map((p) => (
            <label
              key={p.id}
              data-testid={`candeza-catalog-assign-package-option-${p.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 12,
                border: '1px solid',
                borderColor: selected === p.id ? '#6366f1' : '#e2e8f0',
                borderRadius: 8, cursor: 'pointer',
                background: selected === p.id ? '#eef2ff' : 'transparent',
              }}
            >
              <input
                type="radio"
                name="package"
                checked={selected === p.id}
                onChange={() => setSelected(p.id)}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {p.sessions_count} sessions · valid {p.validity_days} days · {formatPaise(p.price)}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={submit}
          isLoading={createMut.isPending}
          disabled={!selected || packages.length === 0}
          data-testid="candeza-catalog-assign-package-submit-btn"
        >
          Assign package
        </Button>
      </div>
    </Modal>
  );
};

